import React, { useEffect, useState } from "react"
import { Provider } from "react-redux"
import { PersistGate } from "redux-persist/integration/react"
import { persistStore } from "redux-persist"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"
import store from "./src/state/createStore"
// import createApolloClient from "./src/gatsby-plugin-apollo/client";

const DEFAULT_RECAPTCHA_SCORE = 0.5

const getInitialRecaptchaState = () => {
	if (typeof window === "undefined") {
		const envKey = process.env.RECAPTCHA_KEY || null
		return {
			status: envKey ? "ready" : "disabled",
			key: envKey,
			scoreThreshold: DEFAULT_RECAPTCHA_SCORE,
		}
	}

	const runtimeConfig = window.__FORMULA_RECAPTCHA
	//console.log('runtimeConfig', runtimeConfig);

	if (runtimeConfig && runtimeConfig.enabled !== false && runtimeConfig.siteKey) {
		return {
			status: "ready",
			key: runtimeConfig.siteKey,
			scoreThreshold:
				typeof runtimeConfig.scoreThreshold === "number"
					? runtimeConfig.scoreThreshold
					: DEFAULT_RECAPTCHA_SCORE,
		}
	}

	if (process.env.RECAPTCHA_KEY) {
		return {
			status: "ready",
			key: process.env.RECAPTCHA_KEY,
			scoreThreshold: DEFAULT_RECAPTCHA_SCORE,
		}
	}

	return {
		status: "loading",
		key: null,
		scoreThreshold: DEFAULT_RECAPTCHA_SCORE,
	}
}

const RootProviders = ({ children }) => {
	const [persister] = useState(() => persistStore(store))
	const [recaptchaState, setRecaptchaState] = useState(getInitialRecaptchaState)

	const { status, key, scoreThreshold } = recaptchaState

	useEffect(() => {
		if (typeof window === "undefined") {
			return
		}

		if (status === "ready" && key) {
			window.__FORMULA_RECAPTCHA = Object.assign(
				{},
				window.__FORMULA_RECAPTCHA || {},
				{
					enabled: true,
					siteKey: key,
					scoreThreshold,
				}
			)
			return
		}

		if (status === "disabled") {
			window.__FORMULA_RECAPTCHA = Object.assign(
				{},
				window.__FORMULA_RECAPTCHA || {},
				{
					enabled: false,
					siteKey: null,
					scoreThreshold,
				}
			)
			return
		}

		if (status !== "loading") {
			return
		}

		let cancelled = false
		const baseUrl = process.env.GATSBY_WP_SITEURL
		const ajaxUrl = `${baseUrl ? baseUrl.replace(/\/$/, "") : ""}/wp-admin/admin-ajax.php?action=tt_form_recaptcha_config`

		const fetchConfig = async () => {
			try {
				const response = await fetch(
					ajaxUrl,
					{
						credentials: "include",
					}
				)

				if (!response.ok) {
					throw new Error(`Request failed with status ${response.status}`)
				}

				const payload = await response.json()

				if (cancelled) {
					return
				}

				const data = payload && payload.data ? payload.data : {}

				if (payload && payload.success && data && data.siteKey) {
					const score =
						typeof data.scoreThreshold === "number"
							? data.scoreThreshold
							: DEFAULT_RECAPTCHA_SCORE

					window.__FORMULA_RECAPTCHA = Object.assign(
						{},
						window.__FORMULA_RECAPTCHA || {},
						{
							enabled: data.enabled !== false,
							siteKey: data.siteKey,
							scoreThreshold: score,
						}
					)

					setRecaptchaState({
						status: "ready",
						key: data.siteKey,
						scoreThreshold: score,
					})

					return
				}

				setRecaptchaState(prev => ({
					status: "disabled",
					key: null,
					scoreThreshold: prev.scoreThreshold,
				}))
			} catch (error) {
				if (cancelled) {
					return
				}

				// eslint-disable-next-line no-console
				console.error("Failed to fetch reCAPTCHA configuration", error)

				setRecaptchaState(prev => ({
					status: "disabled",
					key: null,
					scoreThreshold: prev.scoreThreshold,
				}))
			}
		}

		fetchConfig()

		return () => {
			cancelled = true
		}
	}, [status, key, scoreThreshold])

	const app = (
		<Provider store={store}>
			<PersistGate loading={null} persistor={persister}>
				{() => children}
			</PersistGate>
		</Provider>
	)

	if (status === "ready" && key) {
		return (
			<GoogleReCaptchaProvider reCaptchaKey={key}>
				{app}
			</GoogleReCaptchaProvider>
		)
	}

	return app
}

// eslint-disable-next-line react/display-name,react/prop-types
export default ({ element }) => {
	// const client = createApolloClient();
	// Instantiating store in `wrapRootElement` handler ensures:
	//  - there is fresh store for each SSR page
	//  - it will be called only once in browser, when React mounts

	return <RootProviders>{element}</RootProviders>
}
